import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArticleIcon from '@mui/icons-material/Article';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';

const Guidelines = () => {
  useEffect(() => {
    document.title = 'Author Guidelines - ARC Publication Services LLC';
  }, []);

  const sections = [
    {
      icon: <ArticleIcon color="primary" />,
      title: '1. Originality of Work',
      items: [
        'Manuscripts should be original and must not have been published or submitted for publication in any other form.',
        'Duplicate submission, plagiarism, and misuse of previously published material are not allowed.',
        'All sources, ideas, and contributions should be properly recognized and referenced.'
      ]
    },
    {
      icon: <FormatListNumberedIcon color="primary" />,
      title: '2. Manuscript Format',
      items: [
        'The manuscript should be written in a clear and legible manner.',
        'Appropriate headings, sub-headings, tables, illustrations, and referencing may be used as required.',
        'Generally, a manuscript should consist of a title, author information, abstract, keywords, introduction, methods, results or findings, discussion, conclusion, and references, depending upon the type of article.'
      ]
    },
    {
      icon: <DescriptionIcon color="primary" />,
      title: '3. Abstract & Keywords',
      items: [
        'Provide a brief summary highlighting the purpose, methodology, main results, and significance of the research.',
        'Include relevant keywords that reflect the theme of the manuscript and help readers discover the publication.'
      ]
    },
    {
      icon: <DescriptionIcon color="primary" />,
      title: '4. Reference Style',
      items: [
        'Authors should adhere to the reference format required by the particular journal.',
        'References should be precise, complete, and uniformly formatted throughout the manuscript.',
        'All sources cited in the manuscript must correspond with the list of references.'
      ]
    },
    {
      icon: <GavelIcon color="primary" />,
      title: '5. Peer Review Process',
      items: [
        'Submitted manuscripts are first evaluated by the editors and then reviewed according to the procedure followed by the relevant journal.',
        'ARC utilizes a double-blind peer review process whenever applicable.',
        'Authors may be asked to revise their manuscripts based on reviewers’ comments and recommendations.'
      ]
    },
    {
      icon: <GavelIcon color="primary" />,
      title: '6. Ethical Standards',
      items: [
        'Researchers should uphold high standards of ethical research and publishing behavior and disclose any conflicts of interest.',
        'Fraudulent activities such as fabrication, falsification, plagiarism, duplicate submission, unethical research activities, or manipulation of data are not permitted.',
        'Violations of ethical standards may lead to manuscript rejection or further editorial action.'
      ]
    },
    {
      icon: <ArticleIcon color="primary" />,
      title: '7. Submission Process',
      items: [
        'Manuscripts should be submitted through the official submission system of the relevant ARC Publication journal.',
        'The submission should include complete information about the authors, their affiliations, contact information, and any other documents required by the journal.'
      ]
    },
    {
      icon: <FormatListNumberedIcon color="primary" />,
      title: '8. Article Processing Charges',
      items: [
        'Where applicable, article processing charges and any other publication-related fees will be communicated according to the policies of the relevant journal.',
        'Authors are advised to familiarize themselves with the journal’s policies and applicable fee requirements before submitting their manuscript.'
      ]
    },
    {
      icon: <DescriptionIcon color="primary" />,
      title: '9. Copyright & Licenses',
      items: [
        'Copyright and licensing arrangements are governed by the policies of the individual journal.',
        'Authors should review the applicable copyright, licensing, and open-access terms before submitting their manuscript.'
      ]
    }
  ];

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        background:
          'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',

        '&:before, &:after': {
          content: '""',
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(80px)',
          opacity: 0.6,
          zIndex: 0
        }
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Page Title */}
        <Box
          sx={{
            textAlign: 'center',
            position: 'relative',
            display: 'inline-block',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              color: '#1976d2',
              fontSize: {
                xs: '2rem',
                md: '2.5rem'
              },
              display: 'inline-block'
            }}
          >
            Author Guidelines
          </Typography>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            maxWidth: '1000px',
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
            mt: 6
          }}
        >
          {/* Introduction */}
          <Box
            sx={{
              mb: 5,
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#555',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                textAlign: {
                  xs: 'left',
                  md: 'center'
                }
              }}
            >
              ARC Publication Services LLC encourages the submission of
              innovative and excellent research papers by researchers,
              academics, scholars, and professionals from relevant fields.
              Authors are expected to adhere to the following guidelines to
              ensure an efficient and effective reviewing and publication
              process.
            </Typography>
          </Box>

          {/* Guideline Sections */}
          {sections.map((section, index) => (
            <Paper
              key={index}
              elevation={3}
              sx={{
                mb: 4,
                borderRadius: 2,
                overflow: 'hidden',
                transition:
                  'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 0, 0, 0.05)',

                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow:
                    '0 15px 30px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    flexShrink: 0
                  }}
                >
                  {section.icon}
                </Box>

                {/* Section Title */}
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    color: '#2d3748',
                    fontWeight: 700,
                    m: 0,
                    fontSize: {
                      xs: '1.2rem',
                      md: '1.5rem'
                    },
                    background:
                      'linear-gradient(90deg, #4D49BF, #05DBF2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  {section.title}
                </Typography>
              </Box>

              {/* Section Content */}
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor:
                    'rgba(255, 255, 255, 0.8)'
                }}
              >
                <List disablePadding>
                  {section.items.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <ListItem
                        disableGutters
                        sx={{
                          alignItems: 'flex-start',
                          py: 1
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            mt: '4px'
                          }}
                        >
                          <CheckCircleIcon
                            color="primary"
                            sx={{
                              fontSize: '1rem'
                            }}
                          />
                        </ListItemIcon>

                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            variant: 'body1',
                            color: 'text.secondary',
                            sx: {
                              lineHeight: 1.7,
                              fontSize: {
                                xs: '0.95rem',
                                md: '1rem'
                              }
                            }
                          }}
                        />
                      </ListItem>

                      {idx <
                        section.items.length - 1 && (
                        <Divider
                          component="li"
                          sx={{
                            my: 1,
                            opacity: 0.2
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </Paper>
          ))}

          {/* Closing Statement */}
          <Box
            sx={{
              mt: 2,
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              backgroundColor:
                'rgba(255, 255, 255, 0.9)',
              border:
                '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow:
                '0 5px 20px rgba(0, 0, 0, 0.05)',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#555',
                fontSize: {
                  xs: '0.95rem',
                  md: '1.05rem'
                },
                lineHeight: 1.8
              }}
            >
              These guidelines are intended to support a
              transparent, ethical, and efficient publication
              process with ARC Publication Services LLC while
              maintaining the quality and integrity of
              scholarly research.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Guidelines;